import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Skeleton from '@mui/material/Skeleton'
import Typography from '@mui/material/Typography'
import type { SxProps, Theme } from '@mui/material/styles'
import { GlassCard } from '@components/common/Glass'
import { altColor, altColorLighter, mainColor, mainColorLighter } from 'src/ui/theme/tokens'

type RankedTone = 'neutral' | 'correct' | 'team1' | 'team2'

type RankedItemCardProps = {
  index: number
  title: string
  metric?: string
  tone?: RankedTone
  fullScreen?: boolean
  rightAdornment?: React.ReactNode
  meta?: React.ReactNode
  isDragging?: boolean
  isOverlay?: boolean
  dimmed?: boolean
  showIndex?: boolean
  className?: string
  cardSx?: SxProps<Theme>
  loading?: boolean
}

const getToneStyles = (tone: RankedTone) => {
  if (tone === 'correct')
    return {
      borderColor: '#9fd6e5',
      background: '#e1f5fb',
      badgeBg: '#53d9f5',
      badgeColor: '#163f57',
    }
  if (tone === 'team1')
    return {
      borderColor: '#ecc9a8',
      background: altColorLighter,
      badgeBg: altColor,
      badgeColor: '#693413',
    }
  if (tone === 'team2')
    return {
      borderColor: '#9fd6e5',
      background: mainColorLighter,
      badgeBg: mainColor,
      badgeColor: '#163f57',
    }
  return {
    borderColor: '#d2e2ec',
    background: '#ffffff',
    badgeBg: '#edf4f9',
    badgeColor: '#36516a',
  }
}

export default function RankedItemCard({
  index,
  title,
  metric,
  tone = 'neutral',
  fullScreen = false,
  rightAdornment,
  meta,
  isDragging,
  isOverlay,
  dimmed,
  showIndex = true,
  className,
  cardSx,
  loading = false,
}: RankedItemCardProps): JSX.Element {
  const toneStyles = getToneStyles(tone)
  const dimmedStyles = dimmed
    ? {
        background: '#ffffff',
        borderColor: 'rgba(148,163,184,0.6)',
        badgeBg: '#d1d5db',
        badgeColor: '#6b7280',
      }
    : {}

  const metricVisible = Boolean(metric)
  const [skeletonActive, setSkeletonActive] = useState(loading)

  useEffect(() => {
    if (loading) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSkeletonActive(true)
      return
    }
    const timer = window.setTimeout(() => setSkeletonActive(false), 320)
    return () => window.clearTimeout(timer)
  }, [loading])

  return (
    <GlassCard
      className={className}
      sx={{
        borderRadius: '18px',
        px: 1,
        pr: 0.5,
        boxShadow: isOverlay ? '0 16px 30px #193b5833' : '0 2px 0 #e0eaf1',
        opacity: dimmed ? 0.6 : 1,
        transform: isOverlay ? 'scale(1.015)' : undefined,
        borderColor: dimmedStyles.borderColor || toneStyles.borderColor,
        background: dimmedStyles.background || toneStyles.background,
        transition: 'border-color 180ms ease, background-color 180ms ease',

        '&.card-enter': {
          animation: 'cardEnter 320ms ease-out',
          '@keyframes cardEnter': {
            '0%': { opacity: 0, transform: 'translateY(10px) scale(0.99)' },
            '60%': { opacity: 1, transform: 'translateY(-2px) scale(1.01)' },
            '100%': { opacity: 1, transform: 'translateY(0) scale(1)' },
          },
        },
        '&.miss-warm': {
          border: 'solid 1px rgba(245,158,11,1)',
          boxShadow: '0 3px 0 #e0eaf2, 0 8px 18px #1c3b5709',
        },
        '&.miss-cold': {
          border: 'solid 1px rgba(56,189,248,1)',
          boxShadow: '0 3px 0 #e0eaf2, 0 8px 18px #1c3b5709',
        },
        // '&.miss-neutral': {
        //   boxShadow: 'inset 0 0 10px rgba(148,163,184,0.35)',
        // },
        ...(cardSx || {}),
      }}
    >
      <Box display="flex" alignItems="center" gap={0} flexDirection="row">
        <Box
          display="flex"
          justifyContent="flex-start"
          alignItems="center"
          gap={1}
          mr={0.75}
          py={1}
        >
          {showIndex ? (
            <Box sx={{ position: 'relative', width: 30, height: 30 }}>
              {skeletonActive ? (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    opacity: loading ? 1 : 0,
                    transition: 'opacity 300ms ease',
                  }}
                >
                  <Skeleton variant="circular" width={30} height={30} animation="wave" />
                </Box>
              ) : null}
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  minWidth: 30,
                  height: 30,
                  borderRadius: '11px',
                  backgroundColor: dimmedStyles.badgeBg || toneStyles.badgeBg,
                  color: dimmedStyles.badgeColor || toneStyles.badgeColor,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: 14,
                  opacity: loading || skeletonActive ? 0 : 1,
                  transition: 'opacity 200ms ease',
                }}
              >
                {index}
              </Box>
            </Box>
          ) : null}
          {meta ? <Box>{meta}</Box> : null}
        </Box>
        <Box
          flexGrow={1}
          minWidth={0}
          py={1}
          sx={{
            position: 'relative',
            minHeight: 28,
          }}
        >
          {skeletonActive ? (
            <Box
              sx={{
                opacity: loading ? 1 : 0,
                transition: 'opacity 300ms ease',
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
              }}
            >
              <Skeleton
                variant="text"
                width={`${Math.max(((index + 1) * 9) % 29, 20)}%`}
                height={fullScreen ? 27 : 30}
                sx={{
                  opacity: 0.6,
                }}
                animation="wave"
              />
            </Box>
          ) : null}
          <Box
            sx={{
              opacity: loading || skeletonActive ? 0 : 1,
              transition: 'opacity 300ms ease',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              minHeight: 28,
              flexDirection: 'column',
            }}
          >
            <MarqueeText
              text={title}
              dimmed={!!dimmed}
              fontSize={{
                xs: 14,
                sm: 16,
                md: 16,
              }}
            />
            <Typography
              variant="caption"
              color={dimmed ? '#9ca3af' : '#6b7280'}
              textAlign="left"
              sx={{
                display: 'block',
                maxHeight: metricVisible ? 60 : 0,
                opacity: metricVisible ? 1 : 0,
                transform: metricVisible ? 'translateY(0)' : 'translateY(-6px)',
                transition: 'max-height 260ms ease, opacity 260ms ease, transform 260ms ease',
                overflow: 'hidden',
              }}
            >
              {metric || ''}
            </Typography>
          </Box>
        </Box>
        {rightAdornment ? (
          <Box display="flex" alignItems="center" justifyContent="center" height="100%">
            {rightAdornment}
          </Box>
        ) : null}
      </Box>
    </GlassCard>
  )
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function MarqueeText({ text, fontSize, dimmed }: { text: string; fontSize: any; dimmed: boolean }) {
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const textRef = useRef<HTMLSpanElement | null>(null)
  const [offset, setOffset] = useState(0)

  useLayoutEffect(() => {
    const measure = () => {
      if (!wrapRef.current || !textRef.current) return
      const wrapWidth = wrapRef.current.offsetWidth
      const textWidth = textRef.current.scrollWidth
      const nextOffset = Math.max(0, textWidth - wrapWidth)
      setOffset(nextOffset)
    }
    measure()
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null
    if (observer && wrapRef.current) observer.observe(wrapRef.current)
    if (observer && textRef.current) observer.observe(textRef.current)
    window.addEventListener('resize', measure)
    return () => {
      window.removeEventListener('resize', measure)
      if (observer) observer.disconnect()
    }
  }, [text])

  const shouldScroll = offset > 4
  const moveDuration = Math.max(3, offset / 30)
  const holdStart = 2
  const holdEnd = 2
  const totalDuration = moveDuration + holdStart + holdEnd
  const startHoldPct = (holdStart / totalDuration) * 100
  const endHoldPct = ((holdStart + moveDuration) / totalDuration) * 100

  return (
    <Box
      ref={wrapRef}
      sx={{
        overflow: 'hidden',
        // whiteSpace: 'nowrap',
      }}
    >
      <Typography
        component="span"
        ref={textRef}
        fontWeight={700}
        fontSize={fontSize}
        color={dimmed ? '#9ca3af' : '#111827'}
        sx={{
          display: 'inline-block',
          paddingRight: shouldScroll ? '0' : 0,
          '--marquee-offset': `${offset}px`,
          animation: shouldScroll ? `marquee ${totalDuration}s linear infinite` : 'none',
          '@keyframes marquee': {
            '0%': { transform: 'translateX(0)' },
            [`${startHoldPct}%`]: { transform: 'translateX(0)' },
            [`${endHoldPct}%`]: {
              transform: 'translateX(calc(var(--marquee-offset) * -1))',
            },
            '100%': {
              transform: 'translateX(calc(var(--marquee-offset) * -1))',
            },
          },
        }}
      >
        {text}
      </Typography>
    </Box>
  )
}
